/**
 * Admin invitations page — list / create / revoke invitations for a scope.
 *
 * Route: /admin/invitations
 *
 * Currently mounted under the legacy super_admin admin layout. Scope is
 * picked via UI: institution (Navarasam) → chapter → optional branch.
 *
 * Spec 100 will introduce a proper per-scope admin layout that drives this
 * page from the URL instead of a dropdown.
 */

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  ApiClientError,
  chaptersApi,
  invitationsApi,
  scopeApi,
  type BranchSummary,
  type ChapterSummary,
  type Invitation,
  type ScopeRole,
  type ScopeType,
} from '@/services/api';

const ROLES: ScopeRole[] = ['admin', 'contributor', 'read_only'];

// Branch UI is hidden from invitations for now — the data model still
// supports branch invites but the workflow shipped to admins is chapter-
// centric. Re-enable when we surface branches again.
const SHOW_BRANCH_UI = false;

export function InvitationsPage() {
  const [chapters, setChapters] = useState<ChapterSummary[]>([]);
  const [chaptersLoading, setChaptersLoading] = useState(true);
  const [chaptersError, setChaptersError] = useState<string | null>(null);

  // Institution id — loaded once via scope resolver; needed for institution-scoped invites.
  const [institutionId, setInstitutionId] = useState<string>('');
  const [institutionLabel, setInstitutionLabel] = useState<string>('');

  const [scopeType, setScopeType] = useState<ScopeType>('chapter');
  const [chapterId, setChapterId] = useState<string>('');
  const [branches, setBranches] = useState<BranchSummary[]>([]);
  const [branchId, setBranchId] = useState<string>('');

  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [invitationsError, setInvitationsError] = useState<string | null>(null);

  // Create form state
  const [role, setRole] = useState<ScopeRole>('contributor');
  const [expiresInDays, setExpiresInDays] = useState<number>(30);
  const [maxUses, setMaxUses] = useState<string>('');
  const [suggestedBranchIds, setSuggestedBranchIds] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [lastCreated, setLastCreated] = useState<Invitation | null>(null);

  const selectedScopeId = useMemo(() => {
    if (scopeType === 'branch') return branchId;
    if (scopeType === 'chapter') return chapterId;
    return institutionId;
  }, [scopeType, chapterId, branchId, institutionId]);

  // Load chapters once. Also resolve the institution id so we can create
  // institution-scoped invites from the same page.
  useEffect(() => {
    let cancelled = false;
    setChaptersLoading(true);
    setChaptersError(null);
    Promise.all([scopeApi.resolve('navarasam'), chaptersApi.list('navarasam')])
      .then(([scope, rows]) => {
        if (cancelled) return;
        setInstitutionId(scope.institution.id);
        setInstitutionLabel(scope.institution.displayName);
        setChapters(rows);
        if (rows.length > 0 && !chapterId) {
          setChapterId(rows[0]!.id);
        }
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setChaptersError(err.message);
      })
      .finally(() => {
        if (!cancelled) setChaptersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load branches when chapter changes.
  useEffect(() => {
    let cancelled = false;
    if (!chapterId) {
      setBranches([]);
      return;
    }
    chaptersApi
      .listBranches(chapterId)
      .then((rows) => {
        if (cancelled) return;
        setBranches(rows);
        if (scopeType === 'branch' && rows.length > 0 && !branchId) {
          setBranchId(rows[0]!.id);
        }
      })
      .catch(() => {
        if (!cancelled) setBranches([]);
      });
    return () => {
      cancelled = true;
    };
  }, [chapterId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadInvitations() {
    if (!scopeType || !selectedScopeId) return;
    setInvitationsLoading(true);
    setInvitationsError(null);
    try {
      const rows = await invitationsApi.list(scopeType, selectedScopeId);
      setInvitations(rows);
    } catch (err) {
      setInvitationsError(err instanceof Error ? err.message : 'Failed to load invitations');
    } finally {
      setInvitationsLoading(false);
    }
  }

  useEffect(() => {
    if (selectedScopeId) {
      void loadInvitations();
    } else {
      setInvitations([]);
    }
  }, [scopeType, selectedScopeId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleCreate(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setCreateError(null);
    setLastCreated(null);
    if (!selectedScopeId) {
      setCreateError('Select a scope first');
      return;
    }
    setCreating(true);
    try {
      const { invitation } = await invitationsApi.create({
        scopeType,
        scopeId: selectedScopeId,
        role,
        expiresInDays,
        maxUses: maxUses ? Number(maxUses) : undefined,
        suggestedBranchIds:
          scopeType === 'chapter' && suggestedBranchIds.length > 0 ? suggestedBranchIds : undefined,
      });
      setLastCreated(invitation);
      setSuggestedBranchIds([]);
      setMaxUses('');
      await loadInvitations();
    } catch (err) {
      if (err instanceof ApiClientError) {
        setCreateError(err.message);
      } else {
        setCreateError('Failed to create invitation');
      }
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(id: string) {
    try {
      await invitationsApi.revoke(id);
      await loadInvitations();
    } catch (err) {
      setInvitationsError(err instanceof Error ? err.message : 'Failed to revoke');
    }
  }

  function toggleSuggestedBranch(id: string) {
    setSuggestedBranchIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-display font-bold text-white">Invitations</h1>
        <p className="text-white/60 mt-1">Generate scoped invitation links for new members.</p>
      </div>

      {/* Scope selector */}
      <section className="card">
        <h2 className="text-xl font-semibold text-white mb-4">Scope</h2>
        {chaptersLoading ? (
          <p className="text-white/60">Loading chapters…</p>
        ) : chaptersError ? (
          <p className="text-red-400">{chaptersError}</p>
        ) : (
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">Scope type</label>
              <select
                value={scopeType}
                onChange={(e) => {
                  const v = e.target.value as ScopeType;
                  setScopeType(v);
                  if (v !== 'branch') setBranchId('');
                }}
                className="input"
              >
                <option value="institution">Institution</option>
                <option value="chapter">Chapter</option>
              </select>
            </div>
            {scopeType === 'institution' ? (
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Institution</label>
                <input
                  readOnly
                  value={institutionLabel || 'Navarasam'}
                  className="input bg-white/5 text-white/70"
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Chapter</label>
                <select
                  value={chapterId}
                  onChange={(e) => setChapterId(e.target.value)}
                  className="input"
                >
                  {chapters.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.displayName}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {scopeType === 'branch' && (
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Branch</label>
                <select
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                  className="input"
                  disabled={branches.length === 0}
                >
                  {branches.length === 0 ? (
                    <option value="">— No branches —</option>
                  ) : (
                    branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.displayName ?? b.name}
                      </option>
                    ))
                  )}
                </select>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Create form */}
      <section className="card">
        <h2 className="text-xl font-semibold text-white mb-4">Create invitation</h2>
        <form onSubmit={handleCreate} className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">Role on join</label>
              <select value={role} onChange={(e) => setRole(e.target.value as ScopeRole)} className="input">
                {ROLES.map((r) => (
                  <option key={r} value={r}>{r.replace('_', ' ')}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">Expires in (days)</label>
              <input
                type="number"
                min={1}
                max={365}
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(Math.max(1, Number(e.target.value || 30)))}
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">Max uses (optional)</label>
              <input
                type="number"
                min={1}
                value={maxUses}
                onChange={(e) => setMaxUses(e.target.value)}
                placeholder="unlimited"
                className="input"
              />
            </div>
          </div>

          {SHOW_BRANCH_UI && scopeType === 'chapter' && branches.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                Suggested branches (optional — invitee can pick one on accept)
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-2 bg-white/5 rounded">
                {branches.map((b) => (
                  <label key={b.id} className="flex items-center gap-2 text-sm text-white/80">
                    <input
                      type="checkbox"
                      checked={suggestedBranchIds.includes(b.id)}
                      onChange={() => toggleSuggestedBranch(b.id)}
                    />
                    {b.displayName ?? b.name}
                  </label>
                ))}
              </div>
            </div>
          )}

          {createError && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-red-400 text-sm">{createError}</p>
            </div>
          )}

          <button type="submit" disabled={creating || !selectedScopeId} className="btn-primary">
            {creating ? 'Creating…' : 'Create invitation'}
          </button>
        </form>

        {lastCreated && <NewInviteCallout invite={lastCreated} />}
      </section>

      {/* List */}
      <section className="card">
        <h2 className="text-xl font-semibold text-white mb-4">Existing invitations</h2>
        {invitationsLoading ? (
          <p className="text-white/60">Loading…</p>
        ) : invitationsError ? (
          <p className="text-red-400">{invitationsError}</p>
        ) : invitations.length === 0 ? (
          <p className="text-white/60">No invitations yet for this scope.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-white/60 uppercase text-xs">
                <tr>
                  <th className="px-2 py-2">Code</th>
                  <th className="px-2 py-2">Role</th>
                  <th className="px-2 py-2">Uses</th>
                  <th className="px-2 py-2">Expires</th>
                  <th className="px-2 py-2">Status</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody className="text-white/80">
                {invitations.map((inv) => (
                  <tr key={inv.id} className="border-t border-white/10">
                    <td className="px-2 py-2 font-mono text-xs">
                      <CopyLink code={inv.code} />
                    </td>
                    <td className="px-2 py-2 capitalize">{inv.role.replace('_', ' ')}</td>
                    <td className="px-2 py-2">
                      {inv.useCount}{inv.maxUses ? ` / ${inv.maxUses}` : ''}
                    </td>
                    <td className="px-2 py-2">{new Date(inv.expiresAt).toLocaleDateString()}</td>
                    <td className="px-2 py-2">
                      <StatusBadge invite={inv} />
                    </td>
                    <td className="px-2 py-2 text-right">
                      {inv.isActive && (
                        <button
                          type="button"
                          onClick={() => handleRevoke(inv.id)}
                          className="text-xs text-red-400 hover:text-red-300"
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function NewInviteCallout({ invite }: { invite: Invitation }) {
  const url = `${window.location.origin}/i/${invite.code}`;
  return (
    <div className="mt-4 p-4 bg-primary/10 border border-primary/30 rounded-lg space-y-2">
      <p className="text-sm font-semibold text-white">Invitation created — share this link:</p>
      <div className="flex items-center gap-2">
        <input readOnly value={url} className="input flex-1 font-mono text-xs" />
        <button
          type="button"
          onClick={() => void navigator.clipboard.writeText(url)}
          className="btn-secondary text-xs px-3 py-2"
        >
          Copy
        </button>
      </div>
    </div>
  );
}

function CopyLink({ code }: { code: string }) {
  const url = `${window.location.origin}/i/${code}`;
  return (
    <button
      type="button"
      onClick={() => void navigator.clipboard.writeText(url)}
      title={url}
      className="text-white/70 hover:text-white underline-offset-2 hover:underline"
    >
      {code.slice(0, 10)}…
    </button>
  );
}

function StatusBadge({ invite }: { invite: Invitation }) {
  if (invite.isActive) {
    return <span className="text-green-400 text-xs">Active</span>;
  }
  if (invite.revokedAt) {
    return <span className="text-red-400 text-xs">Revoked</span>;
  }
  if (new Date(invite.expiresAt).getTime() < Date.now()) {
    return <span className="text-white/40 text-xs">Expired</span>;
  }
  if (invite.maxUses && invite.useCount >= invite.maxUses) {
    return <span className="text-white/40 text-xs">Used up</span>;
  }
  return <span className="text-white/40 text-xs">Inactive</span>;
}

export default InvitationsPage;
